import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
    defaultClientId,
    fetchGroupClients,
    getReportFirmId,
    setReportFirm,
} from '../../ClientSelection';
import { BRAND } from '../../CompanyInfo';

/**
 * Firm dropdown for the customer report screens.
 *
 * The choice is stored (see setReportFirm) so it survives navigation between
 * ledger / pending bills / invoices / order list / bounce list / bill tracking,
 * and each screen reads it at fetch time.
 *
 * onChange(firm) fires after a switch so the screen can clear whatever it is
 * showing - those rows belong to the previous firm.
 *
 * onReady(firm) fires once, when the initial firm has been resolved from the
 * group list. The list arrives after mount, so a screen that loads without a
 * View button (pending bills) needs this to know when it can fetch.
 */
const FirmSelect = ({ onChange, onReady, label = 'Firm' }) => {
    const [clients, setClients] = useState([]);
    const [selected, setSelected] = useState('');
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;

        fetchGroupClients()
            .then((list) => {
                if (cancelled) return;
                setClients(list);

                // Keep the stored choice when it is still in the group,
                // otherwise fall back to the customer default firm.
                const stored = getReportFirmId();
                const initial =
                    list.find((c) => c.wClientId === stored) ||
                    list.find((c) => c.wClientId === defaultClientId) ||
                    list[0];

                if (initial) {
                    setReportFirm(initial);
                    setSelected(initial.wClientId);
                    if (onReady) onReady(initial);
                }
            })
            .catch((err) => {
                console.error('Could not load the firm list', err);
                if (cancelled) return;
                setFailed(true);
                toast.error('Could not load the firm list');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const changeHandler = (e) => {
        const id = e.target.value;
        const client = clients.find((c) => c.wClientId === id);
        if (!client) return;
        setReportFirm(client);
        setSelected(id);
        if (onChange) onChange(client);
    };

    return (
        <select
            className='form-control'
            value={selected}
            onChange={changeHandler}
            disabled={loading || failed || clients.length === 0}
            aria-label={label}
            title={label}
            style={{ borderColor: BRAND.green, fontWeight: 600 }}
        >
            <option value="">
                {loading
                    ? 'Loading firms...'
                    : failed
                        ? 'Could not load firms'
                        : clients.length === 0
                            ? 'No firms available'
                            : 'Select firm'}
            </option>
            {clients.map((c) => (
                <option key={c.wClientId} value={c.wClientId}>
                    {c.firmName}
                </option>
            ))}
        </select>
    );
};

export default FirmSelect;
